import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  Grid
} from '@mui/material';
import { 
  Lock as LockIcon, 
  Visibility, 
  VisibilityOff,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { trackPageView, trackFormSubmission } from '../utils/analytics';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  // ?welcome=1: first-time setup from a technician portal invitation
  const isWelcome = new URLSearchParams(useLocation().search).get('welcome') === '1';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  // Track page view
  useEffect(() => {
    trackPageView('/reset-password', 'Reset Password');
  }, []);

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${apiBase}/api/auth/verify-reset-token/${token}`);
        const data = await response.json();

        if (response.ok) {
          setTokenValid(true);
        } else {
          setError(data.error || 'This reset link is invalid or has expired. Please request a new password reset.');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, apiBase]);

  const validatePassword = () => {
    if (!password.trim()) {
      return 'Password is required';
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBase}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        trackFormSubmission('reset_password', true);
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: isWelcome
                ? 'Your password is set. Sign in with your email and new password to open your portal.'
                : 'Password successfully reset. Please log in with your new password.'
            }
          });
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password. Please try again.');
        trackFormSubmission('reset_password', false);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Network error. Please check your connection and try again.');
      trackFormSubmission('reset_password', false);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleRequestNewReset = () => {
    navigate('/login', { 
      state: { 
        openForgotPassword: true 
      }
    });
  };

  if (verifying) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {isWelcome ? 'Checking your invitation...' : 'Verifying Reset Link...'}
            </Typography>
            <LinearProgress />
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!tokenValid && !verifying) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {isWelcome ? 'This invitation has expired' : 'Invalid Reset Link'}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
            )}
            <Typography variant="body1" sx={{ mb: 3 }}>
              {isWelcome
                ? 'Invitation links work for 7 days. If you already set a password, just sign in. Otherwise ask your manager for a new invitation, or use "Forgot password" on the sign-in page.'
                : 'This password reset link is invalid or has expired.'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRequestNewReset}
                >
                  Request New Reset
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGoToLogin}
                >
                  Back to Login
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {isWelcome ? 'Your password is set!' : 'Password Reset Successful!'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your password has been successfully reset. You will be redirected to the login page shortly.
            </Typography>
            <Button
              variant="contained"
              onClick={handleGoToLogin}
              size="large"
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img 
            src="/images/logo2.jpg" 
            alt="PrimeShine Logo" 
            style={{ width: '120px', marginBottom: '1rem' }} 
          />
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            {isWelcome ? 'Welcome! Set your password' : 'Reset Your Password'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isWelcome
              ? 'Choose a password for your technician portal. You will use it with your email to sign in.'
              : 'Enter your new password below'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
            autoFocus
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Password must be at least 6 characters long"
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            required
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !password || !confirmPassword}
            sx={{ mb: 2 }}
          >
            {loading ? 'Saving...' : isWelcome ? 'Set Password' : 'Reset Password'}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={handleGoToLogin}
            disabled={loading}
          >
            Back to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;